/**
 * PATH: packages/ui/src/kanban/components/KanbanBoardBackgroundPicker.tsx
 * FILE_ID: UI-KANBAN-BOARD-BG-PICKER-001
 * PURPOSE: Shared board-background editor — theme presets, a free-form
 *          custom colour, curated scenic images, and a custom image URL.
 * WHY:
 *   FEAT-2026-06-15-KANBAN-BOARD-BG-RIGHTCLICK. Extracted from the inline
 *   block in KanbanBoardView's header "..." menu so the SAME picker powers
 *   BOTH that menu and the new right-click canvas menu (rule 12: reuse,
 *   no fork). Adds the previously-missing free-form colour row (native
 *   colour input + hex field + quick swatches) — the operator's actual
 *   ask: "change the color of the background".
 * FLOW:
 *   current config_json -> operator pick -> onApply(next config_json)
 * DEPENDENCIES:
 *   - ../theme: BOARD_THEMES + BOARD_BG_IMAGES (presets + scenic images).
 *   - ../utils/boardBackground: the pure config-mutation helpers.
 * RELATED:
 *   - ./KanbanBoardCanvasMenu.tsx: embeds this picker on right-click.
 *   - KanbanBoardView.tsx: header "..." menu embeds it too.
 */
import { useState } from 'react'
import { Check } from 'lucide-react'

import { BOARD_BG_IMAGES, BOARD_THEMES } from '../theme'
import {
  BOARD_BG_COLOR_SWATCHES,
  applyBgColor,
  applyBgImageId,
  applyBgImageUrl,
  applyThemePreset,
  clearBackground,
  currentBgColor,
  hasCustomBackground,
  normalizeHexColor,
  type BoardConfig,
} from '../utils/boardBackground'

export interface KanbanBoardBackgroundPickerProps {
  /** The board's current `config_json`. */
  config: BoardConfig | null | undefined
  /** Persist a new `config_json`. Parent wires this to the board-update
   *  mutation; the picker stays open after applying so the operator can
   *  audition several looks. */
  onApply: (next: BoardConfig) => void
  /** Optional: fired after each apply (e.g. close a transient host menu).
   *  The right-click canvas menu omits this (stays open); the header
   *  dropdown passes a close-both-menus handler to match prior UX. */
  onApplied?: () => void
  /** Disable every control while a save is in flight. */
  busy?: boolean
}

export function KanbanBoardBackgroundPicker({
  config,
  onApply,
  onApplied,
  busy,
}: KanbanBoardBackgroundPickerProps) {
  const cfg = (config ?? {}) as {
    theme_preset?: string
    bg_color?: string
    bg_image_id?: string
    bg_image_url?: string
  }
  const savedColor = currentBgColor(config)
  const [hexDraft, setHexDraft] = useState(savedColor || '#d7ecfb')

  const apply = (next: BoardConfig) => {
    onApply(next)
    onApplied?.()
  }

  return (
    <div className="space-y-2 p-2" data-testid="board-bg-picker">
      {/* Theme presets */}
      <p className="text-[10px] uppercase tracking-wide text-neutral-400">Theme</p>
      <div className="grid grid-cols-4 gap-1">
        {BOARD_THEMES.map((t) => {
          const active = cfg.theme_preset === t.preset
          return (
            <button
              key={t.preset}
              type="button"
              disabled={busy}
              onClick={() => apply(applyThemePreset(config ?? {}, t.preset))}
              aria-label={t.preset}
              title={`${t.preset} — applies to canvas, lanes, and cards`}
              className={[
                'h-7 w-full rounded border-2 transition-transform hover:scale-105',
                active ? 'border-blue-500' : 'border-transparent',
                busy ? 'opacity-50' : '',
              ].join(' ')}
              style={{ background: t.bg }}
            />
          )
        })}
      </div>

      {/* Free-form custom colour — the operator's actual ask. */}
      <p className="pt-1 text-[10px] uppercase tracking-wide text-neutral-400">Custom colour</p>
      <div className="grid grid-cols-6 gap-1.5">
        {BOARD_BG_COLOR_SWATCHES.map((hex) => {
          const active = savedColor === hex
          return (
            <button
              key={hex}
              type="button"
              disabled={busy}
              onClick={() => {
                setHexDraft(hex)
                apply(applyBgColor(config ?? {}, hex))
              }}
              aria-label={hex}
              title={hex}
              style={{ background: hex }}
              className={[
                'relative flex h-6 w-full items-center justify-center rounded border-2 transition-transform hover:scale-110',
                active ? 'border-blue-400' : 'border-transparent',
                busy ? 'opacity-50' : '',
              ].join(' ')}
            >
              {active && <Check className="h-3 w-3 text-neutral-700" aria-hidden />}
            </button>
          )
        })}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          aria-label="Pick a custom background colour"
          value={normalizeHexColor(hexDraft) ?? '#d7ecfb'}
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value
            setHexDraft(v)
            apply(applyBgColor(config ?? {}, v))
          }}
          className="h-7 w-9 cursor-pointer rounded border border-neutral-200 bg-transparent p-0.5 disabled:opacity-50 dark:border-zinc-700"
        />
        <input
          type="text"
          aria-label="Background colour hex"
          placeholder="#d7ecfb"
          value={hexDraft}
          disabled={busy}
          onChange={(e) => setHexDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            const norm = normalizeHexColor(hexDraft)
            if (norm) apply(applyBgColor(config ?? {}, norm))
          }}
          className="min-w-0 flex-1 rounded border border-neutral-200 bg-neutral-50 px-1.5 py-1 text-[11px] text-neutral-700 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-neutral-200"
        />
      </div>

      {/* Background image */}
      <p className="pt-1 text-[10px] uppercase tracking-wide text-neutral-400">Background image</p>
      <div className="grid grid-cols-3 gap-1">
        {BOARD_BG_IMAGES.map((img) => {
          const active = (cfg.bg_image_id ?? '') === img.id
          const isPlain = img.id === 'plain'
          return (
            <button
              key={img.id}
              type="button"
              disabled={busy}
              onClick={() => apply(applyBgImageId(config ?? {}, isPlain ? '' : img.id))}
              aria-label={img.label}
              title={img.label}
              className={[
                'relative h-12 w-full overflow-hidden rounded border-2 transition-transform hover:scale-105',
                active ? 'border-blue-500' : 'border-transparent',
                busy ? 'opacity-50' : '',
              ].join(' ')}
              style={
                isPlain
                  ? { background: 'repeating-linear-gradient(45deg, #f1f5f9 0 6px, #e2e8f0 6px 12px)' }
                  : {
                      backgroundImage: `url("${img.url}&w=240")`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
              }
            >
              <span className="absolute bottom-0 inset-x-0 truncate bg-black/40 px-1 py-0.5 text-[9px] font-medium text-white">
                {img.label}
              </span>
            </button>
          )
        })}
      </div>

      {/* Custom image URL */}
      <div className="pt-0.5">
        <label className="block text-[10px] uppercase tracking-wide text-neutral-400">Custom URL</label>
        <input
          type="url"
          placeholder="https://... (jpg/png)"
          defaultValue={cfg.bg_image_url ?? ''}
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            apply(applyBgImageUrl(config ?? {}, (e.currentTarget.value || '').trim()))
          }}
          className="mt-0.5 w-full rounded border border-neutral-200 bg-neutral-50 px-1.5 py-1 text-[11px] text-neutral-700 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-neutral-200"
        />
        <p className="mt-0.5 text-[9.5px] text-neutral-400">Press Enter to apply.</p>
      </div>

      {/* Reset */}
      {hasCustomBackground(config) && (
        <button
          type="button"
          disabled={busy}
          onClick={() => apply(clearBackground(config ?? {}))}
          className="mt-1 w-full rounded border border-neutral-200 px-2 py-1 text-[11px] text-neutral-600 hover:bg-neutral-100 disabled:opacity-50 dark:border-zinc-700 dark:text-neutral-300 dark:hover:bg-zinc-900"
        >
          Reset to default
        </button>
      )}
    </div>
  )
}
